/* NPH v27/v28: progressive enhancement, no dependencies and no tracking calls. */
(() => {
  'use strict';
  class NphSwitcher extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs = [...this.querySelectorAll('[data-nph-tab]')].filter(el => el.closest('nph-switcher') === this);
      this.panels = this.tabs.map(tab => [...this.querySelectorAll('[data-nph-panel]')].find(panel => `#${panel.id}` === tab.getAttribute('href')));
      if (!this.tabs.length || this.panels.some(panel => !panel)) return;
      this.querySelector('[data-nph-tabs]')?.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[i].id);
        this.panels[i].setAttribute('role', 'tabpanel');
        this.panels[i].setAttribute('aria-labelledby', tab.id);
        this.panels[i].tabIndex = 0;
        tab.addEventListener('click', event => { event.preventDefault(); this.select(i); }, options);
        tab.addEventListener('keydown', event => {
          if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); this.select(i); return; }
          let next;
          if (event.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); this.select(next); this.tabs[next].focus(); }
        }, options);
      });
      const target = this.tabs.findIndex(tab => tab.getAttribute('href') === window.location.hash);
      const pet = this.dataset.nphSync === 'pet' ? this.closest('.nph27-home')?.dataset.nphPet : '';
      const synced = pet ? this.tabs.findIndex(tab => tab.dataset.nphPet === pet) : -1;
      this.select(target >= 0 ? target : synced >= 0 ? synced : 0);
      this.classList.add('is-ready');
    }
    select(index, synchronize = true) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.panels[i].hidden = !selected;
      });
      this.panels[index].querySelectorAll('nph-product-rail').forEach(rail => rail.update?.());
      if (synchronize && this.dataset.nphSync === 'pet') {
        const root = this.closest('.nph27-home');
        const pet = this.tabs[index].dataset.nphPet;
        if (root && (pet === 'dog' || pet === 'cat')) {
          root.dataset.nphPet = pet;
          root.querySelectorAll('nph-switcher[data-nph-sync="pet"]').forEach(peer => {
            if (peer === this || !peer.tabs) return;
            const match = peer.tabs.findIndex(tab => tab.dataset.nphPet === pet);
            if (match >= 0) peer.select(match, false);
          });
        }
      }
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class NphProductRail extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.rail = this.querySelector('[data-nph-rail]');
      this.previous = this.querySelector('[data-nph-prev]');
      this.next = this.querySelector('[data-nph-next]');
      if (!this.rail || !this.previous || !this.next) return;
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.rail.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      if (window.ResizeObserver) {
        this.observer?.disconnect();
        this.observer = new ResizeObserver(() => this.update());
        this.observer.observe(this.rail);
      } else window.addEventListener('resize', () => this.update(), options);
      this.classList.add('is-ready');
      this.update();
    }
    move(direction) {
      const card = this.rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      const distance = (card?.getBoundingClientRect().width || this.rail.clientWidth * .75) + gap;
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.rail.scrollBy({ left: direction * distance, behavior: reduced ? 'auto' : 'smooth' });
    }
    update() {
      if (!this.rail) return;
      this.previous.disabled = this.rail.scrollLeft < 2;
      this.next.disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 2;
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }

  class NphCompareTray extends HTMLElement {
    connectedCallback() {
      if (document.querySelector('npc-compare')) { this.hidden = true; return; }
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.items = this.querySelector('[data-nph-compare-items]');
      this.count = this.querySelector('[data-nph-compare-count]');
      this.link = this.querySelector('[data-nph-compare-link]');
      this.status = this.querySelector('[data-nph-compare-status]');
      this.labels = {};
      this.handles = [];
      this.read();
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-nph-remove]');
        if (remove) {
          const handle = remove.dataset.nphRemove;
          this.toggle(remove.dataset.nphRemove);
          if (!this.hidden) (this.querySelector('[data-nph-remove]') || this.link).focus();
          else [...document.querySelectorAll('[data-npc-compare-product]')].find(button => button.dataset.npcCompareProduct === handle)?.focus();
        }
        if (event.target.closest('[data-nph-clear]')) {
          const first = this.handles[0];
          this.handles = []; this.save(); this.render();
          [...document.querySelectorAll('[data-npc-compare-product]')].find(button => button.dataset.npcCompareProduct === first)?.focus();
        }
      }, options);
      window.addEventListener('storage', event => {
        if (event.key === 'npc:compare-products' || event.key === 'nph:compare-labels' || event.key === null) { this.read(); this.render(); }
      }, options);
      document.addEventListener('shopify:section:load', () => this.syncButtons(), options);
      this.observer?.disconnect();
      this.observer = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-npc-compare-product]') || node.querySelector('[data-npc-compare-product]'))))) this.syncButtons();
      });
      const main = document.querySelector('main');
      if (main) this.observer.observe(main, { childList: true, subtree: true });
      this.render();
    }
    read() {
      try {
        const stored = JSON.parse(localStorage.getItem('npc:compare-products') || '[]');
        this.handles = Array.isArray(stored) ? [...new Set(stored.filter(h => typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(h)))].slice(0,3) : [];
        const labels = JSON.parse(localStorage.getItem('nph:compare-labels') || '{}');
        this.labels = labels && typeof labels === 'object' && !Array.isArray(labels) ? labels : {};
      } catch (_) { this.handles = []; this.labels = {}; }
    }
    save() {
      try {
        localStorage.setItem('npc:compare-products', JSON.stringify(this.handles));
        const labels = Object.fromEntries(this.handles.map(handle => [handle, this.label(handle)]));
        localStorage.setItem('nph:compare-labels', JSON.stringify(labels));
      } catch (_) { /* URL carries the selection even when storage is disabled. */ }
    }
    label(handle) {
      return typeof this.labels[handle] === 'string' ? this.labels[handle].slice(0,180) : handle.replace(/-/g, ' ');
    }
    toggle(handle, title) {
      if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle || '')) return;
      this.status.textContent = '';
      if (this.handles.includes(handle)) this.handles = this.handles.filter(item => item !== handle);
      else {
        if (this.handles.length >= 3) {
          this.status.textContent = 'Du har valt tre produkter. Ta bort en för att välja en ny.';
          this.querySelector('details')?.setAttribute('open', '');
          return;
        }
        this.handles.push(handle);
        if (title) this.labels[handle] = String(title).slice(0,180);
      }
      this.save(); this.render();
      if (this.handles.length) this.status.textContent = `${this.handles.length} av 3 produkter valda.`;
    }
    syncButtons() {
      document.querySelectorAll('[data-npc-compare-product]').forEach(button => {
        if (button.closest('npc-compare')) return;
        const selected = this.handles.includes(button.dataset.npcCompareProduct);
        button.setAttribute('aria-pressed', String(selected));
        button.textContent = selected ? 'Vald för jämförelse −' : 'Jämför +';
        button.setAttribute('aria-label', `${selected ? 'Ta bort från jämförelse' : 'Jämför'} ${button.dataset.nphProductTitle || button.dataset.npcCompareProduct.replace(/-/g,' ')}`);
      });
    }
    render() {
      if (!this.items) return;
      this.hidden = this.handles.length === 0;
      this.count.textContent = String(this.handles.length);
      this.items.replaceChildren();
      this.handles.forEach(handle => {
        const li = document.createElement('li'), label = document.createElement('span'), remove = document.createElement('button');
        label.textContent = this.label(handle);
        remove.type = 'button'; remove.dataset.nphRemove = handle; remove.textContent = '×';
        remove.setAttribute('aria-label', `Ta bort ${this.label(handle)} från jämförelsen`);
        li.append(label, remove); this.items.append(li);
      });
      const url = new URL(this.link.dataset.nphCompareUrl, location.origin);
      url.searchParams.set('products', this.handles.join(','));
      this.link.href = `${url.pathname}${url.search}`;
      this.syncButtons();
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }
  if (!customElements.get('nph-switcher')) customElements.define('nph-switcher', NphSwitcher);
  if (!customElements.get('nph-product-rail')) customElements.define('nph-product-rail', NphProductRail);
  if (!customElements.get('nph-compare-tray')) customElements.define('nph-compare-tray', NphCompareTray);
})();
